import { useEffect, lazy, Suspense } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import PanelContainer from './PanelContainer';
import { PanelTitleProvider } from './PanelTitleContext';
import { useLayoutStore } from '@shared/hooks/useLayoutStore';
import { PATHS } from '@app/routes/paths';
import type { PanelRoute } from '@app/routes/types';

const MapView = lazy(() => import('@map/MapView'));

const panelTitles: Record<string, string> = {
  [PATHS.STATUS]: 'Status',
  [PATHS.ROUTES]: 'Routes',
  [PATHS.HOLDINGS]: 'Holdings',
  [PATHS.CATALOG]: 'Catalog',
  [PATHS.ORDERS]: 'Orders',
  [PATHS.SUPPORT]: 'Support',
  [PATHS.BASKET]: 'Basket',
};

const pathToPanelRoute: Record<string, PanelRoute> = {
  [PATHS.STATUS]: 'status',
  [PATHS.ROUTES]: 'routes',
  [PATHS.HOLDINGS]: 'holdings',
  [PATHS.CATALOG]: 'catalog',
  [PATHS.ORDERS]: 'orders',
  [PATHS.SUPPORT]: 'support',
  [PATHS.BASKET]: 'basket',
};

function getPanelTitle(pathname: string): string {
  for (const [path, title] of Object.entries(panelTitles)) {
    if (pathname.startsWith(path)) return title;
  }
  return 'Panel';
}

function getPanelRoute(pathname: string): PanelRoute | null {
  for (const [path, route] of Object.entries(pathToPanelRoute)) {
    if (pathname.startsWith(path)) return route;
  }
  return null;
}

export default function PanelLayout() {
  const location = useLocation();
  const activePanel = useLayoutStore((s) => s.activePanel);
  const panelPinned = useLayoutStore((s) => s.panelPinned);
  const setActivePanel = useLayoutStore((s) => s.setActivePanel);

  // Sync active panel with URL on direct navigation / page load
  useEffect(() => {
    const panelRoute = getPanelRoute(location.pathname);
    if (panelRoute && activePanel !== panelRoute) {
      setActivePanel(panelRoute);
    }
  }, [location.pathname, activePanel, setActivePanel]);
  const title = getPanelTitle(location.pathname);

  return (
    <Box
      data-testid="layout-panel"
      sx={{ display: 'flex', flex: 1, position: 'relative', overflow: 'hidden' }}
    >
      {activePanel && (
        <PanelTitleProvider>
          <PanelContainer title={title}>
            <Outlet />
          </PanelContainer>
        </PanelTitleProvider>
      )}

      {/* Map */}
      <Box
        id="map"
        data-testid="map-container"
        sx={{
          flex: 1,
          position: 'relative',
          // When panel is unpinned (overlay), map takes full width
          ...(!panelPinned && activePanel ? { ml: 0 } : {}),
        }}
      >
        <Suspense fallback={null}>
          <MapView />
        </Suspense>
      </Box>
    </Box>
  );
}
